import * as React from 'react'

import {
  CheckCircle2,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Send,
  Smartphone,
  UserMinus,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { IUserRow } from '../types'

export interface UserRowActionHandlers {
  onEdit: (userId: string) => void
  onInvite: (userId: string) => void
  onApprove: (userId: string) => void
  onSetNotInvited: (userId: string) => void
  onResetPassword: (userId: string) => void
  onManagePhone: (userId: string) => void
}

export interface UserRowActionItem {
  key: string
  label: string
  icon: React.ReactNode
  disabled: boolean
  onClick: () => void
}

/**
 * Returns the per-row action items for a user, with disable rules derived
 * from the row's state. Pulled into a hook so a future context-menu
 * (right-click on a row) can reuse the same item set without duplicating
 * the disable logic.
 */
export function useUserRowActionItems(
  row: IUserRow,
  handlers: UserRowActionHandlers,
): UserRowActionItem[] {
  const t = Localization.translator
  // `canBeSetNotInvited` reflects UserProject.isAccepted, which is the
  // same precondition the legacy code used for Reset Password.
  const isAccepted = row.canBeSetNotInvited
  return [
    {
      key: 'edit',
      label: t.edit_verb,
      icon: <Pencil />,
      disabled: false,
      onClick: () => handlers.onEdit(row.id),
    },
    {
      key: 'invite',
      label: t.invite,
      icon: <Send />,
      disabled: !row.canBeInvited,
      onClick: () => handlers.onInvite(row.id),
    },
    {
      key: 'approve',
      label: t.approve_verb,
      icon: <CheckCircle2 />,
      disabled: !row.canBeApproved,
      onClick: () => handlers.onApprove(row.id),
    },
    {
      key: 'setNotInvited',
      label: t.setNotInvited,
      icon: <UserMinus />,
      disabled: !isAccepted,
      onClick: () => handlers.onSetNotInvited(row.id),
    },
    {
      key: 'resetPassword',
      label: t.resetPassword,
      icon: <KeyRound />,
      disabled: !isAccepted,
      onClick: () => handlers.onResetPassword(row.id),
    },
    {
      key: 'managePhone',
      // TODO(STX1-637): move to LocalizationManager once DES-73 copy is final.
      label: 'Manage Phone Number',
      icon: <Smartphone />,
      disabled: !row.phone,
      onClick: () => handlers.onManagePhone(row.id),
    },
  ]
}

interface IUserRowActionsMenuProps {
  row: IUserRow
  handlers: UserRowActionHandlers
}

/**
 * 3-dot menu trigger + dropdown content. Renders the items from
 * `useUserRowActionItems`. The trigger swallows clicks so the underlying
 * row's onClick (which opens the Edit modal) doesn't also fire.
 */
export function UserRowActionsMenu({
  row,
  handlers,
}: IUserRowActionsMenuProps): React.ReactElement {
  const items = useUserRowActionItems(row, handlers)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={Localization.translator.options}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            <MoreHorizontal />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="tw:w-44">
        {items.map(item => (
          <DropdownMenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={e => {
              e.stopPropagation()
              item.onClick()
            }}
          >
            {item.icon}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
